import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { usePWA } from '@/hooks/usePWA';

const PWAInstallPrompt: React.FC = () => {
  const { canInstall, isIOS, isStandalone, installPWA } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if user has previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      setIsDismissed(true);
    }

    // Show prompt after 30 seconds if not dismissed and can install
    const timer = setTimeout(() => {
      if (!dismissed && (canInstall || (isIOS && !isStandalone))) {
        setShowPrompt(true);
      }
    }, 30000);

    return () => clearTimeout(timer);
  }, [canInstall, isIOS, isStandalone]);

  const handleInstall = async () => {
    if (canInstall) {
      const result = await installPWA();
      if (result) {
        setShowPrompt(false);
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setIsDismissed(true);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed or dismissed
  if (isStandalone || isDismissed || !showPrompt) {
    return null;
  }

  // iOS specific instructions
  if (isIOS && !canInstall) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
        <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 shadow-xl">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-white/80 hover:text-white"
          >
            <X size={20} />
          </button>

          <div className="flex items-start space-x-3">
            <Smartphone className="flex-shrink-0 mt-1" size={24} />
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Install Pushing Pencils</h3>
              <p className="text-sm text-white/90 mb-3">
                Get the full app experience! Tap the share button{' '}
                <span className="inline-block mx-1">⬆️</span>
                and select "Add to Home Screen"
              </p>
              <Button
                size="sm"
                variant="secondary"
                className="bg-white text-blue-600 hover:bg-gray-100"
                onClick={handleDismiss}
              >
                Got it!
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Standard install prompt for supported browsers
  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
      <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 shadow-xl">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/80 hover:text-white"
        >
          <X size={20} />
        </button>

        <div className="flex items-start space-x-3">
          <Download className="flex-shrink-0 mt-1" size={24} />
          <div className="flex-1">
            <h3 className="font-semibold mb-1">Install Pushing Pencils</h3>
            <p className="text-sm text-white/90 mb-3">
              Install our app for offline access, faster loading, and the best experience!
            </p>
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white text-blue-600 hover:bg-gray-100"
                onClick={handleInstall}
                leftIcon={<Download size={16} />}
              >
                Install App
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-white/50 text-white hover:bg-white/10"
                onClick={handleDismiss}
              >
                Not Now
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export { PWAInstallPrompt };
